import React, { FC, useRef, useEffect, useState, Fragment } from 'react'
import { useDispatch } from 'react-redux'
import { PageHeader, Space, BackTop } from 'antd'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
    barChart1Option, barChart2Option, barChart3Option, barChart4Option, barChart5Option
} from '@/helpers/barChart'
import { transPercent } from '@/utils/util'
import PageBreadcrumb from '@/components/pageBreadcrumb'

// 引入 echarts 核心模块
import * as echarts from 'echarts/core'
import { LegendComponent, GridComponent, TooltipComponent, TitleComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
// 按需引入图表类型
import { BarChart } from 'echarts/charts'
import { useSelector } from 'react-redux'
import type { Dispatch } from 'redux'
import { IAppState } from '@/store/reducers'
import { activities, allTech, coreTech, getDicOfClass, keyTech, priorities } from '@/services/table.service'
import type { IClassOptionResData } from '@/store/models/tableEdit.model'
import { setDicClassList } from '@/store/actions/tableEdit.action'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import _ from 'lodash'
import Nav from '@/components/nav'

// 注册必须的组件
echarts.use([
    BarChart, CanvasRenderer,
    LegendComponent, GridComponent, TooltipComponent, TitleComponent
])

export interface ICallbackType {
    // eslint-disable-next-line no-unused-vars
    (ticket: string, html: string): void
}

export interface ILegendData {
    type: string
    name: string
    stack?: string
    smooth?: boolean
    label?: {
        show: boolean
        textBorderColor?: string
    }
    barMaxWidth?: number
    barMinHeight?: number
    showBackground?: boolean
    backgroundStyle?: any
    data: number[]
}
interface ISetGotDic {
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    (a: boolean): void
}

export const getXAxis = (res: any[], workgroupList: any[]): string[] => {
    const xAxis: string[] = []
    // const xAxisObj = {}
    workgroupList.forEach(workgroupItem => {
        // if (!(workgroupItem.groupName in xAxisObj)) {
        //     const findObj = res.find(resItem => resItem.workGroupName === workgroupItem.groupName)
        //     if (findObj) {
        //         xAxisObj[findObj.workGroupName] = true
        //         xAxis.push(findObj.workGroupName)
        //     }
        // }
        xAxis.push(workgroupItem.groupName)
    })
    return xAxis
}
export const getLegend = ({ legend, xAxis, findCont, stack, labelShow }: {
    legend: string[] | number[]
    xAxis: string[]
    findCont: any
    stack?: string
    labelShow?: boolean
}): any[] => {
    const series: ILegendData[] = []
    legend.forEach(legendItem => {
        const _obj: ILegendData = {
            type: 'bar',
            name: legendItem + '',
            barMaxWidth: 40,
            label: {
                show: false
            },
            data: []
        }
        if (stack) {
            _obj.stack = stack
        }
        if (labelShow) {
            (_obj.label as {show: boolean}).show = labelShow
        }

        xAxis.forEach(xAxisItem => {
            const findObj = findCont(xAxisItem, legendItem)
            if (findObj) {
                _obj.data.push(findObj.total)
            } else {
                _obj.data.push(0)
            }
        })

        series.push(_obj)
    })
    return series
}

// 获取字典列表，作为下拉选项
export const getDicClass = async (dispatch: Dispatch, setGotDic: ISetGotDic): Promise<void> => {
    const dicArr = ['release status', 'research form', 'priority', 'status-year', 'tmt approval',
        'maturity', 'requirement source', '相关BU', '技术成熟度', '专利状态']
    const promiseArr = dicArr.map(item => getDicOfClass({
        className: item,
        status: 1
    }))
    try {
        const resArr = await Promise.all<Promise<IClassOptionResData[]>>(promiseArr)
        const obj: {
            [propertys: string]: IClassOptionResData[]
        } = {}
        dicArr.forEach((item, index) => {
            obj[item] = resArr[index]
        })
        dispatch(setDicClassList(obj))
        setGotDic(true)
    } catch (error: any) {
        const str = getErrorStr(error)
        dispatch(requestFail(str))
    }
}

const IndexPage: FC = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const { t } = useTranslation()
    
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)
    // 是否已获取过dicClassList
    const [gotDic, setGotDic] = useState(false)
    const [years, setYears] = useState<number[]>([])
    const [title3, setTitle3] = useState('')
    const [activitiesData, setActivitiesData] = useState<any[]>([])
    const [activitiesDataStatus, setActivitiesDataStatus] = useState(false)
    const route = useSelector((state: IAppState) => state.router)

    const chartRef1 = useRef(null)
    const chartRef2 = useRef(null)
    const chartRef3 = useRef(null)
    const chartRef4 = useRef(null) // 关键技术开发状态
    const chartRef42 = useRef(null) // 核心技术开发状态
    const chartRef5 = useRef(null)
    const chartRef6 = useRef(null)
    const chartRef7 = useRef(null)
    const chartRef8 = useRef(null)
    const chartRef9 = useRef(null)
    const chartRef10 = useRef(null)
    const chartInstanceArr = useRef<any[]>([])

    const viewProjectInfo = (url: string) => {
        const link = document.createElement('a')
        link.style.display = 'none'
        link.target = '_blank'
        link.href = url
        document.body.appendChild(link)
        link.click()
        URL.revokeObjectURL(link.href)
        document.body.removeChild(link)
    }

    const setChart1 = async () => {
        const chart1 = chartRef1.current
        if (chart1) {
            const res = await allTech()
            const lastYear = new Date().getFullYear() - 1
            const legend = [lastYear]
            for (let i = 0; i < 5; i++) {
                legend.push(legend[i] + 1)
            }
            const xAxis: string[] = getXAxis(res, workgroupList)
            const findCont = (xAxisItem: string, legendItem: number | string) => {
                const findObj = res.find(resItem => resItem.workGroupName === xAxisItem && resItem.year === legendItem)
                return findObj || false
            }
            const series: ILegendData[] = getLegend({ legend, xAxis, findCont })
            barChart1Option.legend.data = legend.map(item => item + '')
            barChart1Option.xAxis.data = xAxis
            barChart1Option.series = series
            const chartInstance = echarts.init(chart1)
            chartInstanceArr.current = [...chartInstanceArr.current, chartInstance]
            chartInstance.setOption(barChart1Option)
            chartInstance.on('click', 'series', params => {
                const curWorkgroup = workgroupList.find(item => item.groupName === params.name)
                viewProjectInfo(`projectInfo?workGroupId=${curWorkgroup?.id}&year=${params.seriesName}`)
            })
        }
    }
    const setChart2 = async () => {
        const chart2 = chartRef2.current
        if (chart2) {
            const res = await priorities()
            let legend: string[] = []
            if (Object.keys(dicClassList).length > 0) {
                res.reduce((prev: Record<string, any>, item) => {
                    if (!(item.priority in prev)) {
                        prev[item.priority] = true
                        legend.push(item.priority)
                    }
                    return prev
                }, {})
            } else {
                legend = dicClassList['priority'].map(item => item.name)
            }
 
            const findCont = (xAxisItem: string, legendItem: number | string) => {
                const findObj = res.find(resItem => resItem.workGroupName === xAxisItem && resItem.priority === legendItem)
                return findObj || false
            }
            const xAxis: string[] = getXAxis(res, workgroupList)
            const series: ILegendData[] = getLegend({ legend, xAxis, findCont, stack: 'bar1', labelShow: true })
        
            barChart2Option.legend.data = legend.reverse()
            barChart2Option.xAxis.data = xAxis
            barChart2Option.series = series as any
            const chartInstance2 = echarts.init(chart2)
            chartInstanceArr.current = [...chartInstanceArr.current, chartInstance2]
            const arr = barChart2Option.series.map(item => item.data)
            // 把数字转换为百分比
            const arr3: Array<number>[] = transPercent(arr, barChart2Option.legend.data.length)
            const seriesData = barChart2Option.series.map((item, index) => ({
                ...item,
                data: arr3.map(subItem => subItem[index])
            }))
            chartInstance2.setOption({
                ...barChart2Option,
                label: {
                    // 把百分比显示回原数据
                    formatter(params: any) {
                        try {
                            return barChart2Option.series[params.seriesIndex].data[params.dataIndex]
                        } catch (error) {
                            // console.error(params)
                            // return params.data
                        }
                    }
                },
                tooltip: {
                    ...barChart2Option.tooltip,
                    // 把百分比显示回原数据
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                    formatter(params: any[], ticket: string, callback: ICallbackType) {
                        const result: string[] = params.map((item: any) => {
                            const value = barChart2Option.series[item.seriesIndex].data[item.dataIndex]
                            return `${item.marker}${item.seriesName}: ${value} ${item.data}%`
                        })
                        // console.log(params, ticket, result.join('<br/>'))
                        // setTimeout(() => {
                        //     callback(ticket, result.join('<br/>'))
                        // }, 0)
                        return params[0].axisValue + '<br/>' + result.reverse().join('<br/>')
                    }
                },
                series: seriesData
            })
            chartInstance2.on('click', 'series', params => {
                const curWorkgroup = workgroupList.find(item => item.groupName === params.name)
                viewProjectInfo(`projectInfo?workGroupId=${curWorkgroup?.id}&priority=${params.seriesName}`)
            })
        }
    }
    const setChart3 = async () => {
        const chart3 = chartRef3.current
        if (chart3) {
            const res = await activities()
            setActivitiesData(res)
            setActivitiesDataStatus(true)
            const xAxis: string[] = getXAxis(res, workgroupList)
            const lastYear = new Date().getFullYear() - 1
            const legend = [lastYear + '(On)', lastYear + '(Plan)']
            for (let i = 0; i < 5; i++) {
                const prevYear = lastYear + i
                legend.push((prevYear + 1) + '(On)')
                legend.push((prevYear + 1) + '(Plan)')
            }
            const findCont = (xAxisItem: string, legendItem: number | string) => {
                const legendItemStr = '' + legendItem
                const findObj = res.find(resItem => resItem.workGroupName === xAxisItem 
                    && ('' + legendItemStr).includes(resItem.year)
                    && (
                        (legendItemStr.includes('Plan') && resItem.status === 1)
                        || (legendItemStr.includes('On') && resItem.status === 2)
                    ))
                return findObj || false
            }
            const series: ILegendData[] = getLegend({ legend, xAxis, findCont })
            barChart3Option.legend.data = legend.map(item => item + '')
            barChart3Option.xAxis.data = xAxis
            barChart3Option.series = series
            const chartInstance3 = echarts.init(chart3)
            chartInstanceArr.current = [...chartInstanceArr.current, chartInstance3]
            chartInstance3.setOption(barChart3Option)
            chartInstance3.on('click', 'series', params => {
                const curWorkgroup = workgroupList.find(item => item.groupName === params.name)
                const year = params.seriesName?.substring(0, 4) || ''
                const planStatus = params.seriesName?.substring(5, params.seriesName.length - 1) || ''
                viewProjectInfo(`projectInfo?workGroupId=${curWorkgroup?.id}&year=${year}&planStatus=${planStatus}`)
            })
        }
    }
    const setChart4 = async (core?: string) => {
        let chart4: any = chartRef4.current
        if (core) {
            chart4 = chartRef42.current
        }
        if (chart4) {
            const _barChart4Option = _.cloneDeep(barChart4Option)
            const chartInstance4 = echarts.init(chart4)
            chartInstanceArr.current = [...chartInstanceArr.current, chartInstance4]
            let res: any[]
            if (core) {
                _barChart4Option.title = {
                    ..._barChart4Option.title,
                    text: 'CORE TECHNOLOGY DEVELOPMENT STATUS'
                }
                res = await coreTech()
            } else {
                res = await keyTech()
            }
            if (res.length > 0) {
                let legend: string[] = [..._barChart4Option.legend.data]
                // res.reduce((prev, item) => {
                //     if (!(item.maturity in prev)) {
                //         prev[item.priority] = true
                //         legend.push(item.maturity)
                //     }
                //     return prev
                // }, {})
                if (Object.keys(dicClassList).length > 0) {
                    legend = dicClassList['maturity'].map(item => item.name)
                }
    
                const findCont = (xAxisItem: string, legendItem: number | string) => {
                    const findObj = res.find(resItem => resItem.workGroupName === xAxisItem && resItem.maturity === legendItem)
                    return findObj || false
                }
                const xAxis: string[] = getXAxis(res, workgroupList)
                const series: ILegendData[] = getLegend({ legend, xAxis, findCont, stack: 'bar1', labelShow: true })

                _barChart4Option.legend.data = legend
                _barChart4Option.xAxis.data = xAxis
                _barChart4Option.series = series as any
                const arr = series.map(item => item.data)
                // 把数字转换为百分比
                const arr3: Array<number>[] = transPercent(arr, _barChart4Option.legend.data.length)
                const seriesData = series.map((item, index) => ({
                    ...item,
                    data: arr3.map(subItem => subItem[index])
                }))
                chartInstance4.setOption({
                    ..._barChart4Option,
                    label: {
                        // 把百分比显示回原数据
                        formatter(params: any) {
                            try {
                                return series[params.seriesIndex].data[params.dataIndex]
                            } catch (error) {
                                // console.error(params)
                                // return params.data
                            }
                        }
                    },
                    tooltip: {
                        ..._barChart4Option.tooltip,
                        // 把百分比显示回原数据
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                        formatter(params: any[], ticket: string, callback: ICallbackType) {
                            const result: string[] = params.map((item: any) => {
                                const value = series[item.seriesIndex].data[item.dataIndex]
                                return `${item.marker}${item.seriesName}: ${value} ${item.data}%`
                            })
                            return params[0].axisValue + '<br/>' + result.join('<br/>')
                        }
                    },
                    series: seriesData
                })
            } else {
                const xAxis: string[] = getXAxis([], workgroupList)
                _barChart4Option.xAxis.data = xAxis
                chartInstance4.setOption({
                    ..._barChart4Option,
                    series: _barChart4Option.legend.data.map(legendItem => ({
                        type: 'bar',
                        name: legendItem,
                        stack: 'bar1',
                        barMaxWidth: 40,
                        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
                        data: _barChart4Option.xAxis.data.map(_ => 0)
                    }))
                })
            }
            chartInstance4.on('click', 'series', params => {
                const curWorkgroup = workgroupList.find(item => item.groupName === params.name)
                let techLevel
                if (core) {
                    techLevel = 'core'
                } else {
                    techLevel = 'key'
                }
                const maturity = params.seriesName
                viewProjectInfo(`projectInfo?workGroupId=${curWorkgroup?.id}&techLevel=${techLevel}&maturity=${maturity}`)
            })
        }
    }
    const getYearSeries = (year: number) => {
        const legend = [`${year}(On)`, `${year}(Plan)`]
        const xAxis = workgroupList.map(workgroupItem => workgroupItem.groupName)
        const findCont = (xAxisItem: string, legendItem: number | string) => {
            const legendItemStr = '' + legendItem
            const findObj = activitiesData.find(resItem => resItem.workGroupName === xAxisItem 
                && ('' + legendItemStr).includes(resItem.year)
                && (
                    (legendItemStr.includes('Plan') && resItem.status === 1)
                    || (legendItemStr.includes('On') && resItem.status === 2)
                ))
            return findObj || false
        }
        const series: ILegendData[] = getLegend({ legend, xAxis, findCont })
        return series
    }

    const routes = [
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/charts',
            breadcrumbName: t('charts'),
            icon: 'LineChartOutlined'
        },
    ]
    useEffect(() => {
        // 最近6年各工作组活动状态
        const prevYear = new Date().getFullYear() - 1
        const _year = []
        for (let i = 0; i < 6; i++) {
            _year.push(prevYear + i)
        }
        setYears([..._year])
        setTitle3(t('chartTitle3').replace('2021', '' + _year[0]).replace('2026', '' + _year.pop()))

        // 避免已获取过后，还是无数据的情况
        if (!gotDic && Object.keys(dicClassList).length === 0) {
            getDicClass(dispatch, setGotDic)
        } else {
            setGotDic(true)
        }

        window.onresize = () => {
            chartInstanceArr.current.forEach(item => {
                item.resize()
            })
        }
        return () => {
            window.onresize = null
        }
    }, [])
    useEffect(() => {
        if (activitiesDataStatus) {
            years.forEach((year, index) => {
                const _barChart5Option = { ...barChart5Option }
                const chart5 = [chartRef5, chartRef6, chartRef7, chartRef8, chartRef9, chartRef10][index].current
                if (chart5) {
                    const chartInstance = echarts.init(chart5)
                    chartInstanceArr.current = [...chartInstanceArr.current, chartInstance]
                    const xAxis: string[] = getXAxis([], workgroupList)
                    _barChart5Option.xAxis.data = xAxis
                    _barChart5Option.title.text = `DESAY SV ITMT TEC ACTIVITY YR ${year}`
                    _barChart5Option.legend.data = [`${year}(On)`, `${year}(Plan)`]
                    
                    const series = getYearSeries(year)
                    chartInstance.setOption({
                        ..._barChart5Option,
                        series
                    })
                    chartInstance.on('click', 'series', params => {
                        const curWorkgroup = workgroupList.find(item => item.groupName === params.name)
                        console.log('click series params: ', params, curWorkgroup?.id, params.seriesName)
                        const year = params.seriesName?.substring(0, 4) || ''
                        const planStatus = params.seriesName?.substring(5, params.seriesName.length - 1) || ''
                        viewProjectInfo(`projectInfo?workGroupId=${curWorkgroup?.id}&year=${year}&planStatus=${planStatus}`)
                    })
                }
            })
        }
    }, [years, activitiesDataStatus])
    useEffect(() => {
        if (gotDic) {
            console.log('dicClassList: ', dicClassList)
            setChart1()
            setChart2()
            setChart3()
            setChart4()
            setChart4('core')
        }
    }, [gotDic])
    return (
        <>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            <Space direction="vertical" size={0} style={{ display: 'flex' }}>
                <PageHeader className='chart_title'
                    onBack={() => null}
                    title={t('chartTitle1')}
                />
                <div ref={chartRef1} style={{ width: '100%', height: 500, background: '#fff' }}></div>
                <PageHeader className='chart_title'
                    onBack={() => null}
                    title={t('chartTitle2')}
                />
                <div ref={chartRef2} style={{ width: '100%', height: 500, background: '#fff' }}></div>
                <PageHeader className='chart_title'
                    onBack={() => null}
                    title={title3}
                />
                <div ref={chartRef3} style={{ width: '100%', height: 500, background: '#fff' }}></div>
                <PageHeader className='chart_title'
                    onBack={() => null}
                    title={t('keyTechnologyTitle')}
                />
                <div ref={chartRef4} style={{ width: '100%', height: 500, background: '#fff' }}></div>
                <PageHeader className='chart_title'
                    onBack={() => null}
                    title={t('coreTechnologyTitle')}
                />
                <div ref={chartRef42} style={{ width: '100%', height: 500, background: '#fff' }}></div>
                {years.map((year, index) => (
                    <Fragment key={year}>
                        <PageHeader className='chart_title'
                            onBack={() => null}
                            title={`${t('activityTitle')} (${year}${t('year')})`}
                        />
                        <div ref={[chartRef5, chartRef6, chartRef7, chartRef8, chartRef9, chartRef10][index]}
                            style={{ width: '100%', height: 500, background: '#fff' }}
                        ></div>
                    </Fragment>
                ))}
            </Space>
            <BackTop />
        </>
    )
}

export default IndexPage
